/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, ViewChild } from "@angular/core";

import { ActivatedRoute } from "@angular/router";

import { fromEvent, Subject, Subscription, } from "rxjs";
import { map, debounceTime} from "rxjs/operators";

import { ObjectInstance } from "app/models/object-instance";
import { ObjectService } from "app/services/object.service";
import { EventInstance } from "app/models/event-instance";
import { ImageService } from "app/services/image.service";
import { InstanceFunctionDialogComponent } from "app/components/locate/instance-function-dialog/instance-function-dialog.component";
import { InstanceDataDialogComponent } from "app/components/locate/instance-data-dialog/instance-data-dialog.component";
import { InstanceClickedEvent, NewMapComponent } from "app/components/map/new-map.component";
import { ObjectCategory } from "app/models/object-category";
import { EventUuid_AssetEnteredLocation, EventUuid_AssetEnteredZone, EventUuid_AssetExitedLocation, EventUuid_AssetExitedZone, EventUuid_AssetMovement } from "app/event-constants";
import { ObjectProperty } from "app/models/object-property";
import { PagedResults } from "app/models/paged-results";
import { MonitorAssets_SelectedAssetWebSocketService } from "app/services/monitor-assets-selected-asset-web-socket-service";
import { LocationInstance } from "app/models/location-instance";
import { LocationService } from "app/services/location.service";
import { PropertyType } from "app/models/property-type";
import { PagingControls } from "app/models/paging-controls";
import { CookieService } from "ngx-cookie-service";
import { SaveAssetListDialogComponent } from "./save-asset-list-dialog/save-asset-list-dialog.component";
import { SimpleConfirmDialogComponent } from "app/components/simple-confirm-dialog/simple-confirm-dialog.component";

/** Declare $ to support bootstrap calls in code */
declare var $: any;

/** 
 * Number of Assets to Load / Display on initial display and when searching.  
 * This is the maximum number of Assets that will be shown to the User for selecting. 
 * */
export const MonitorAssets_AssetsPerPage: number = 15;

@Component({
  templateUrl: './monitor-assets.component.html',
  styleUrls: ['./monitor-assets.component.css']
})
export class MonitorAssetsComponent implements OnInit, OnDestroy {

  /** MapComponent for viewing the location chosen inside the Locate Component */
  @ViewChild('locateMap') mapComponent: NewMapComponent;

  /** Reference to the AssetFunctionDialog used to execute Function Action(s) on Assets */
  @ViewChild(InstanceFunctionDialogComponent) instanceFunctionDialogComponent: InstanceFunctionDialogComponent;

  /** Reference to the AssetDataDialog used to execute the "Edit Data" Action on Assets */
  @ViewChild(InstanceDataDialogComponent) instanceDataDialogComponent: InstanceDataDialogComponent;

  /** Reference to the SimpleConfirmDialog used to confirm User Actions before going through with them */
  @ViewChild(SimpleConfirmDialogComponent) simpleConfirmDialogComponent: SimpleConfirmDialogComponent;

  /** Assets to display in the "Select Assets" Tab.  These are the initial Assets to select loaded on load, and any that match the "search" value */
  assetsToSelect: ObjectInstance[] = [];

  /** Assets selected to be shown in the List and traced at the Location / Zone level. */
  assetsSelected: ObjectInstance[] = [];

  /** Assets selected to be shown in the List and have passed any "Search" criteria that is going on.  This are the ones that are actually displayed in the List. */
  selectedAssetsToDisplay: ObjectInstance[] = [];

  /** Holds the Assets being toggled from the "Select Assets" Tab to the "Assets" tab.  */
  selectAssetsBeingToggled: Map<number, ObjectInstance> = new Map();

  /** Holds the Assets being selected in the "Selected Assets" tab */
  assetsSelectedBeingToggled: Map<number, ObjectInstance> = new Map();

  /** Holds the Assets in the "Selected Assets" List tied to their Web Socket connection  */
  assetsBeingTracked: Map<number, WsConnection> = new Map();

  /** Holds the Location thta is currently being displayed */
  locationBeingDisplayed: LocationInstance = null;

  /** Holds the Locations that are available for display.  The "locationBeingDisplayed" will be one of these. */
  locationsToDisplay: LocationInstance[] = [];

  /** Boolean Flag used to hold whether we are selecting / unselecting all the Assets in the "Search Assets" List */
  isSelectingAllAssetsToSelect = false;

  /** Boolean Flag used to hold wheter we are selecting / unselecting all the Assets in the "Selected Assets" List */
  isSelectingAllSelectedAssets = false;

  /**
   * Components related to the "Select Assets" Search Functionality
   */
  // HTML Input Element used for the Asset search value
  @ViewChild('selectAssetsSearchInput') selectAssetsSearchInput!: ElementRef;
  // Boolean Flag used to indicate if we are currently querying the backend for "Search Asset(s) to select" requests. Used to show a "Loading" indicator to the User.
  isSearchingAssetsToSelect = false;
  // Value entered for searching Assets
  assetsToSelectSearchValue = "";
  // Options available for searching Assets
  assetsToSelectSearchOptions: SearchOption[] = [];
  // Selected Search Option for Assets
  selectedAssetsToSelectSearchOption: SearchOption;
  // Boolean flag used to indicate if the Search Option for the Select Assets tab is changing. (Used to disable some UI while things are loaded)
  isAssetsToSelectSearchOptionChanging = false;

  /**
   * Components related to the "Select Assets" Search Functionality
   */
  // HTML Input Element used for the Asset search value
  @ViewChild('selectedAssetsSearchInput') selectedAssetsSearchInput!: ElementRef;
  // Boolean Flag used to indicate if we are currently querying the backend for "Search Asset(s) to select" requests. Used to show a "Loading" indicator to the User.
  isSearchingSelectedAssets = false;
  // Value entered for searching Assets
  selectedAssetsSearchValue = "";
  // Selected Search Option for Assets
  selectedAssetsSearchOption: SearchOption;
  // Boolean flag used to indicate if the Search Option for the Select Assets tab is changing. (Used to disable some UI while things are loaded)
  isSelectedAssetsSearchOptionChanging = false;

  /*
   * Components related to the "Saved Asset Lists" functionality
   */
  // Dialog used to have the User enter the name when saving an Asset List
  @ViewChild(SaveAssetListDialogComponent) saveAssetListDialogComponent: SaveAssetListDialogComponent;
  // Stored Asset List array of the Asset Lists that are available / saved
  storedAssetList: StoredAssetListItem[] = [];
  // Flag which holds whether an Asset List is being deleted
  isDeletingStoredAssetList = false;
  // Flag which holds whether an Asset List is being saved
  isSavingCurrentAssetList = false; 
  // Flag which holds whether an Asset List is being loaded
  isLoadingAssetList = false;
  // Reference to the last Asset List loaded or saved.  Used to populate the default "name" in the SaveAssetListDialog
  lastLoadedOrSavedAssetList: StoredAssetListItem = null;
  // Stored Asset List Item selected to be deleted.  Remembered here while the "Confirm" Dialog is displayed / handled.
  assetListBeingDeleted: StoredAssetListItem = null;

  /** Total # of Asssets. (Used to figure out # of pages) */
  totalNumberOfAssets = 0;
  /** Current Page being displayed for the "Encoding Scheme" Types */
  currentPage= 1;
  /** Exposes MonitorAssets_AssetsPerPage to the Template (module-level constants aren't visible there) */
  assetsPerPage = MonitorAssets_AssetsPerPage;
    
  /** 
   * Constructor
   */
  constructor(
    private objectService: ObjectService,
    private locationService: LocationService,
    private imageService: ImageService,
    private route: ActivatedRoute,
    private selectedAssetWebSocketService: MonitorAssets_SelectedAssetWebSocketService,
    private cookieService: CookieService
  ) { }

  /** 
   * OnInit() - Called after the UI is initialized, but before it is displayed. 
   */
  ngOnInit(): void {
    // Grab the Data that was resolved in the background as the page was loading...
    this.route.data.subscribe(
      (data: { 
        initialAssets: PagedResults<ObjectInstance>,
        assetSearchProperties: ObjectProperty[]
      }) => { 
        // Load the initial Assets to display 
        const initialAssets: PagedResults<ObjectInstance> = data.initialAssets;
        if (initialAssets?.data != null) {
          this.assetsToSelect = initialAssets.data;
          this.totalNumberOfAssets = initialAssets.size;
        }
        

        // Load the Search Options for Assets
        {
          const searchOptions: SearchOption[] = [];
          // Add the default "Name" Option
          searchOptions.push(new SearchOption());
          // Add the options for each search property found
          data.assetSearchProperties?.forEach(assetSearchProperty => {
            searchOptions.push(new SearchOption(assetSearchProperty));
          });
          this.assetsToSelectSearchOptions = searchOptions;

          // Set the default Search Option to the NAME option (always the first one)
          this.selectedAssetsToSelectSearchOption = searchOptions[0];
          this.selectedAssetsSearchOption = searchOptions[0];
        }
      });

    /*
     * Setup the Stored Asset List (Saved in a cookie)
     */
    const value = this.cookieService.get('page[monitor-assets]-key[saved-assets-list]');
    console.log("Stored Value: " + value);
    if (value?.length > 0) {
      this.storedAssetList = JSON.parse(value);
    } else {
      this.saveStoredAssetList();
    }
  }

  /** 
   * AfterViewInit() - Called after OnInit and after the UI is displayed. 
   * Use this to interact with the UI elements
   */
  ngAfterViewInit(): void {
    /*
     * Set up the "Select Asset(s) Search" functionality
     */
    fromEvent(this.selectAssetsSearchInput.nativeElement, 'keyup')
      .pipe(
        // On any Key Up Event, set that we are searching Assets to Select and return the value
        map((event: any) => {
          this.isSearchingAssetsToSelect = true;
          return event.target.value;
        }),

        // Debounce to limit REST API requests.  Give the User half a second to stop typing before starting to search. 
        debounceTime(500)
      )
      .subscribe((text: string) => {
        // Perform the Search
        this.searchAssetsToSelect();
      });

      /*
       * Set up the "Selected Asset(s) Search" functionality
       */
    fromEvent(this.selectedAssetsSearchInput.nativeElement, 'keyup')
      .pipe(
        // On any Key Up Event, set that we are searching Assets to Select and return the value
        map((event: any) => {
          this.isSearchingSelectedAssets = true;
          return event.target.value;
        }),

        // Debounce to limit REST API requests.  Give the User half a second to stop typing before starting to search. 
        debounceTime(500)
      )
      .subscribe((text: string) => {
        this.refreshDisplayedSelectedAssetsInList();
        this.isSearchingSelectedAssets = false;
      });
  }

  /** 
   * Called when the Component is being destroyed and used to cleanup resources.
   * Currently closes all WebSockets used for the selected Assets.
   */
  ngOnDestroy(): void {
    // Need to close all open web sockets...
    this.selectAllAssetsToSelect(false);
  }

  /**
   * Called by the UI when the "Select Assets" List's Search Option 
   * is changed.  This re-searches the data if needed. 
   */
  onAssetsToSelectSearchOptionChanged(): void {
    // If the "Select Assets" List is being searched, nothing needs to be done.
    if (this.isSearchingAssetsToSelect) {
      return;
    }
    // If the Search Value isn't specified, then we aren't displaying search results and nothing needs to be done.
    else if (this.assetsToSelectSearchValue?.length == 0) {
      return;
    }

    // Re-search the list
    this.isAssetsToSelectSearchOptionChanging = true;
    this.isSearchingAssetsToSelect = true;
    this.searchAssetsToSelect();
  }

  /**
   * Performs the Search Functionality for the "Select Assets" List.
   * 
   * This queries the backend for a new page of Assets depending on the
   * selected Search Option and the Search Value.  
   */
  async searchAssetsToSelect(): Promise<void> {
    // Any new Search starts back on Page 1
    this.currentPage = 1;

    // Load Page 1 of the Assets to Select for the current Search Option / Value
    await this.loadAssetsToSelectPage(this.currentPage);

    // Update the Flags used to control some UI properties
    this.isSearchingAssetsToSelect = false;
    this.isAssetsToSelectSearchOptionChanging = false;
  }

  /**
   * Fetches the given Page of Assets to Select from the backend, based on the
   * currently selected Search Option / Value, and updates "assetsToSelect" /
   * "totalNumberOfAssets" with the results.
   *
   * Used both when performing a new Search (always Page 1) and when the User
   * pages through the "Select Assets" List (onPageChange).
   */
  private async loadAssetsToSelectPage(pageNumber: number): Promise<void> {
    // Get the current search values for the request
    const searchOption = this.selectedAssetsToSelectSearchOption;
    const searchValue = this.assetsToSelectSearchValue;
    console.log(`Searching Assets to Select with Search Option: '${searchOption?.searchType}/${searchOption?.property?.name}', Search Value: '${searchValue}', Page: ${pageNumber}`);

    // Get the Search Type, if there is no search value set the Search Type to NAME as it should display everything.
    let searchType = searchOption?.searchType;
    if (searchValue == null || searchValue.length == 0) {
      searchType = SearchType.NAME;
    }

    // Compute the starting index (0 based) for the Page being requested
    const start = (pageNumber - 1) * MonitorAssets_AssetsPerPage;

    // Variable used to hold the Assets being retrieved from the backend...
    const assets: ObjectInstance[] = [];

    // Perform the Search based on the Search Type
    switch (searchType) {
      case SearchType.PROPERTY: {
        // Property Search - Search against a Asset's Data Value for a specific Property
        try {
          const pagingControls = new PagingControls();
          pagingControls.start = start;
          pagingControls.size = MonitorAssets_AssetsPerPage;
          pagingControls.order = "ASC";

          const pagedData = await this.objectService.propertySearchInstances(searchOption?.property?.uuid, searchValue, pagingControls);
          if (pagedData?.data != null) {
            assets.push(...pagedData.data);
            this.totalNumberOfAssets = pagedData?.size;
          }
        } catch (error) {
          console.error("Problem search Assets by Property.", error);
        }
        break;
      }
      case SearchType.NAME:
      default: {
        // Name Search - Search against the Asset's Name
        try {
          const pagedData = await this.objectService.getAllObjectInstancesForCategoryPaging(ObjectCategory.ASSET, start, MonitorAssets_AssetsPerPage, "ASC", searchValue);
          if (pagedData?.data != null) {
            assets.push(...pagedData.data);
            this.totalNumberOfAssets = pagedData?.size;
          }
        } catch (error) {
          console.error("Problem searching Assets by Name.", error);
        }
        break;
      }
    }

    // Update the Assets being displayed for selection
    this.assetsToSelect = assets;
  }

  /**
   * Returns a boolean indicating if all the Assets in the "Search Assets" List
   * have been selected to be in the "Selected Assets" List where they are tracked. 
   */
  isAllAssetsToSelectSelected(): boolean {
    if (this.assetsToSelect.length == 0) {
      return false;
    }
    return this.assetsToSelect.find(asset => !this.assetsBeingTracked.has(asset?.id)) == null;
  }

  /**
   * Tied to the UI Checkbox at the top of the "Search Assets" List. 
   * 
   * This is used to either select all or unselect all of the currently
   * listed "Search Asset"s.  
   */
  selectAllAssetsToSelect(select: boolean): void {
    this.isSelectingAllAssetsToSelect = true;
    this.selectAllAssetsToSelectAsync(select);
  }

  /**
   * Handles the "Select All" and "Unselect All" process for the "Search Assets" List.
   * This either adds all or removes all of the Assets currently in the Search List to 
   * the "Selected Assets" List depending on the given "select" parameter.
   */
  private async selectAllAssetsToSelectAsync(select: boolean): Promise<void> {
    console.log(`Selecting All Search Assets: ${select}`);

    const promises: Promise<void>[] = [];
    if (select) {
      this.assetsToSelect.forEach(asset => {
        if (!this.assetsBeingTracked.has(asset.id)) {
          this.selectAssetsBeingToggled.set(asset.id, asset);
          promises.push(this.addSelectAssetToAssetList(asset, true)) ;
        }
      });
    } else {
      this.assetsToSelect.forEach(asset => {
        if (this.assetsBeingTracked.has(asset.id)) {
          this.selectAssetsBeingToggled.set(asset.id, asset);
          promises.push(this.removeSelectAssetFromAssetList(asset)) ;
        }
      });
    }
    await Promise.all(promises);

    this.isSelectingAllAssetsToSelect = false;
  }

  /**
   * Called by the UI when an "Searched" Asset is checked / unchecked.
   * This either adds or removes the Asset from the selected Assets
   * in the other Tab.  
   * 
   * This also either starts or stops tracking the Asset by opening 
   * or closing a Web Socket.  It starts with onlylistening for 
   * "Entered / Exited" / "Location / Zone" Events but can be sent a 
   * message to toggle the "Movement" Events as well. 
   * 
   * This essentially opens a Web Socket connection for every Asset in
   * that list, which may be a problem when there are a bunch. 
   */
  onAssetToSelectToggled(asset: ObjectInstance, event): void {
    console.log(`On Select Asset Toggled!  Asset: '${asset?.id}/${asset?.name}', Checked: ${event?.target?.checked}`);
    if (asset?.id == null) {
      console.warn("Ignoring Select Asset being toggled since ID was not found.");
      return;
    }

    // Add the Asset being selected to the Map of Assets being toggled....
    this.selectAssetsBeingToggled.set(asset.id, asset);

    // Depending on if this is a checked / unchecked event, add / remove the Select Asset from the Assets List
    if (event?.target?.checked) {
      this.addSelectAssetToAssetList(asset, true);
    } else {
      this.removeSelectAssetFromAssetList(asset);
    }
  }

  /**
   * Adds the given Asset to the "Selected Assets" List.
   */
  private async addSelectAssetToAssetList(asset: ObjectInstance, refresh: boolean): Promise<void> {
    // Get a fresh version of the Asset being selected and refresh the Lists if needed....
    if (refresh) {
      let freshAsset: ObjectInstance = null;
      try {
        freshAsset = await this.objectService.getObjectInstanceForId(asset.id);
      } catch (error) {
        console.warn("Problem obtaining fresh data on Asset being selected in the Select Assets Tab.  Possibly stale data will be used.");
      }
      if (freshAsset != null) {
        asset = freshAsset;
        this.updateDisplayedAsset(asset);
      }
    }

    // Add the Selected Asset to the assetsSelected[] and sort it
    this.assetsSelected.push(asset);
    this.assetsSelected.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      } else if (a.name == b.name) {
        return 0;
      } else {
        return 1;
      }
    });

    // Start tracking the Asset
    if (this.assetsBeingTracked.has(asset.id)) {
      console.warn("NOT creating WS connection for Asset as it is already being tracked...");
    } else {
      const wsConnection = new WsConnection();
      wsConnection.wsSubject = this.selectedAssetWebSocketService.create(asset.id);
      wsConnection.wsSubscription = wsConnection.wsSubject.subscribe(
        message => { 
           this.handleAssetWebSocketMessage(message, asset); 
        },
        error => {
          console.error(`Error detected opening WS Connection for Asset: '${asset.id}/${asset.name}'!`, error);
          wsConnection.close();
        }
      );
      this.assetsBeingTracked.set(asset.id, wsConnection);

      this.refreshDisplayedSelectedAssetsInList();
    }

    // Remove the Asset from the Map of Select Assets which are being toggled.
    this.selectAssetsBeingToggled.delete(asset.id);
  }

  /**
   * Removes the given Asset from the "Selected Assets" List
   */
  private async removeSelectAssetFromAssetList(asset: ObjectInstance): Promise<void> {
    // Remove the Asset from the Selected Assets List
    this.assetsSelected = this.assetsSelected.filter(a => a?.id != asset?.id);

    // Stop tracking the Asset
    if (!this.assetsBeingTracked.has(asset.id)) {
      console.warn("NOT destroying WS connection for Asset as it doesn't exist.");
    } else {
      if (this.assetsBeingTracked.get(asset.id).movementEventsEnabled) {
        await this.stopTrackingSelectedAsset(asset);
      }

      const wsConnection = this.assetsBeingTracked.get(asset.id);
      wsConnection.close();
      this.assetsBeingTracked.delete(asset.id);
    }

    // Remove the Asset from the Map of Select Assets which are being toggled.
    this.selectAssetsBeingToggled.delete(asset.id);

    this.refreshDisplayedSelectedAssetsInList();
  }

  /**
   * Returns a boolean indicating if the givne Asset is being 
   * toggled between states for the "Search Assets" and "Selected Assets".
   * 
   * Used by the UI to disable the checkbox in the "Search Assets" List
   * if the state is being changed.
   */
  isAssetToSelectCheckboxBeingToggled(asset: ObjectInstance): boolean {
    return this.selectAssetsBeingToggled.has(asset?.id);
  }

  /**
   * Returns a boolean indicating if all the Assets in the "Selected Assets"
   * List have themselves been selected to be tracked.  This is used by the UI
   * to determine if the "ALL" checkbox should be checked.
   */
  isAllSelectedAssetsSelected(): boolean {
    if (this.assetsSelected.length == 0) {
      return false;
    }
    return this.assetsSelected.find(asset => !this.assetsBeingTracked.get(asset.id).movementEventsEnabled) == null;
  }

  /**
   * Starts the "Select All" / "Unselect All" Assets in the "Selected Assets"
   * List.   
   * 
   * This sets a boolean flag and kicks off the async process.
   */
  selectAllSelectedAssets(select: boolean): void {
    if (!this.isSelectingAllSelectedAssets) {
      this.isSelectingAllSelectedAssets = true;
      this.selectAllSelectedAssetsAsync(select);
    }
  }

  /**
   * This is the "Select All" / "Unselect All" Process for the Assets in the 
   * "Selected Assets" List that runs in the background.
   * 
   * This either adds any that are missing to the map displays on the left,
   * or removes any that are drawn depending on the situation.  Then it 
   * resets the boolean flag indicating that the process is running (for UI 
   * purposes)
   */
  private async selectAllSelectedAssetsAsync(select: boolean): Promise<void> {
    console.log(`Selecting All Selected Assets: ${select}`);

    // Kick off processes to either start / stop tracking any unselected / selected Assets depending on the appropriate situation
    const promises: Promise<void>[] = [];
    if (select) {
      this.selectedAssetsToDisplay.forEach(asset => {
        if (!this.assetsBeingTracked.get(asset.id).movementEventsEnabled) {
          this.assetsSelectedBeingToggled.set(asset.id, asset);
          promises.push(this.startTrackingSelectedAsset(asset));
        }
      });
    } else {
      this.selectedAssetsToDisplay.forEach(asset => {
        if (this.assetsBeingTracked.get(asset.id).movementEventsEnabled) {
          this.assetsSelectedBeingToggled.set(asset.id, asset);
          promises.push(this.stopTrackingSelectedAsset(asset));
        }
      });
    }

    // Wait for all the kicked off processes to finish...
    if (promises.length > 0) {
      await Promise.all(promises);
    }

    // Update the "isSelectingAllSelectedAssets" back to false to indicate the process is no longer running
    this.isSelectingAllSelectedAssets = false;
  }

  /**
   * Called by the UI when a "Selected Asset"'s checkbox is toggled to 
   * either start or stop being tracked / displayed in a Map on the RHS.  
   */
  onSelectedAssetToggled(asset: ObjectInstance, event): void {
    console.log(`On Selected Asset Toggled!  Selected Asset: '${asset?.id}/${asset?.name}', Checked: ${event?.target?.checked}`);
    if (asset?.id == null) {
      console.warn("Ignoring Selected Asset being toggled since ID was not found.");
      return;
    }

    // Add the Asset being selected to the Map of Assets being toggled....
    this.assetsSelectedBeingToggled.set(asset.id, asset);

    // Depending on if this is a checked / unchecked event, handle the selection...
    if (event?.target?.checked) {
      this.startTrackingSelectedAsset(asset);
    } else {
      this.stopTrackingSelectedAsset(asset);
      this.refreshDisplayedSelectedAssetsInList();
    }
  }

  /**
   * Called by the UI when the selected "Search Option" for the
   * search on the "Selected Assets" List is changed.
   * 
   * Re-searches the list if needed. 
   */
  onSelectedAssetSearchOptionChanged(): void {
    // If the "Selected Assets" List is being searched, nothing needs to be done.
    if (this.isSearchingSelectedAssets) {
      return;
    }
    // If the Search Value isn't specified, then we aren't displaying search results and nothing needs to be done.
    else if (this.selectedAssetsSearchValue.length == 0) {
      return;
    }

    // Re-search the list
    this.isSelectedAssetsSearchOptionChanging = true; 
    this.refreshDisplayedSelectedAssetsInList();
    this.isSelectedAssetsSearchOptionChanging = false;
  }

  /**
   * Starts tracking the given Asset in a Map on the RHS
   */
  private async startTrackingSelectedAsset(asset: ObjectInstance): Promise<void> {
    // Retrieve a more "current" version of the Asset being tracked
    let freshAsset: ObjectInstance = null;
    try {
      freshAsset = await this.objectService.getObjectInstanceForId(asset.id);
    } catch (error) {
      console.warn("Problem obtaining fresh data on Asset being tracked.  Possibly stale data will be used.", error);
    }
    if (freshAsset != null) {
      asset = freshAsset;
      this.updateDisplayedAsset(asset);
    }
    
    // Add the Asset's Location to the Display (if needed)
    if (asset.locationInstance != null) {
      await this.addLocationToDisplay(asset.locationInstance);
    }

    // Draw the Asset (if needed)
    if (this.locationBeingDisplayed != null && this.locationBeingDisplayed?.id == asset?.locationInstance?.id) {
      await this.mapComponent.addAssetToDisplay(asset);
    }

    // Enable the Movement Events for the Asset
    this.assetsBeingTracked.get(asset?.id)?.enableMovementEvents();

    // Remove the Asset from the Map of Selected Assets which are being toggled.
    this.assetsSelectedBeingToggled.delete(asset.id);
  }

  /**
   * Stops tracking the given Asset in a Map on the RHS
   */
  private async stopTrackingSelectedAsset(asset: ObjectInstance): Promise<void> {
    // Disable the Movement Evnets for the Asset since we are no longer tracking it
    this.assetsBeingTracked.get(asset?.id)?.disableMovementEvents();

    // If the currently drawn Location is the Location the Asset was in...
    if (asset?.locationInstance?.id == this.locationBeingDisplayed?.id) {
      // Remove the Asset from the Map
      await this.mapComponent.removeAssetFromDisplay(asset);
    }

    // If the Map contains no other Assets, remove it so that another map may be drawn or a blank page if no other one exists.
    if (this.getSelectedAssetsInLocation(asset.locationInstance).length == 0) {
      await this.removeLocationFromDisplay(asset.locationInstance);
    }

    // Remove the Asset from the Map of Selected Assets which are being toggled.
    this.assetsSelectedBeingToggled.delete(asset.id);
  }

  /**
   * Called by the UI to return a boolean indicating if the 
   * given Asset is being toggled between either being tracked 
   * in a map on the RHS or not.  
   * 
   * Used to disable the checkboxin the "Selected Assets" List 
   * while this is being done.
   */
  isSelectedAssetBeingToggled(asset: ObjectInstance): boolean {
    return this.assetsSelectedBeingToggled.has(asset?.id);
  }

  /**
   * Adds the given Location to the Display available to be a Map
   * on the RHS.  If no Map is currently displayed, it becomes the 
   * active map. 
   */
  private async addLocationToDisplay(location: LocationInstance): Promise<void> {
    if (location?.id == null) {
      console.warn("NOT Adding Location to Display since ID could not be determined.");
      return;
    }
    if (this.locationsToDisplay.find(dl => dl?.id == location.id) != null) {
      console.warn("NOT Adding Location to Display since it is already added.");
      return;
    }

    // Add the Location to the Location's being displayed...
    this.locationsToDisplay.push(location);

    // Get a fresh instance of the Location from the backend (Call REST API)
    let freshLocation: LocationInstance = null;
    try {
      freshLocation = await this.locationService.getLocationInstanceById(location.id);
    } catch (error) {
      console.error(`Problem obtaining fresh information about Location to be displayed: '${location?.id}/${location?.name}'`, error);
    }
    // If a instance was obtained, update the local reference.  If not just continue with the stale data.
    if (freshLocation != null) {
      location.zones = freshLocation.zones;
    }

    // If no Location is currently being displayed, display the new Location
    if (this.locationBeingDisplayed == null) {
      this.locationBeingDisplayed = location;
      await this.drawLocation(location);
    }
  }

  /**
   * Removes the given Location as an option from the Locations that can be displayed.
   */
  private async removeLocationFromDisplay(location: LocationInstance): Promise<void> {
    console.log(`Removing Location: '${location?.id}/${location?.name}' from display...`);
    const displayedLocation = this.locationsToDisplay.find(displayedLocation => displayedLocation?.id == location?.id);
    if (displayedLocation == null) {
      console.warn("NOT removing Location from display as it isnt' currently being displayed!");
      return;
    }

    // Remove the Location from the array of displayed locations
    this.locationsToDisplay = this.locationsToDisplay.filter(displayedLocation => displayedLocation?.id != location?.id);

    // If the Location being removed was the displayed Location, display the next one in line
    if (this.locationBeingDisplayed?.id == displayedLocation.id) {
      const newLocationToDisplay = this.locationsToDisplay.length > 0? this.locationsToDisplay[0] : null;
      this.setLocationBeingDisplayed(newLocationToDisplay);
    }
  }

  /**
   * Called when a Location option being displayed as a Button at the top
   * of the Map is clicked.  This should switch to displaying the clicked 
   * on Location.
   */
  onLocationClicked(location: LocationInstance): void {
    console.log(`Location Clicked!  Location: '${location?.id}/${location?.name}'`);
    this.setLocationBeingDisplayed(location);
  }

  /**
   * This updates the current active Map to the given Location.
   */
  private async setLocationBeingDisplayed(location: LocationInstance): Promise<void> {
    console.log(`Setting displayed Location to: '${location?.id}/${location?.name}'...`);
    if ((location == null && this.locationBeingDisplayed == null) || (location?.id == this.locationBeingDisplayed?.id)) {
      console.warn("NOT changing displayed Location as it is not changing ")
      return;
    }

    // Erase the current Location being displayed
    await this.eraseMap();

    // Update the reference to the Location being displayed
    this.locationBeingDisplayed = location;

    // Draw the Location being displayed
    if (this.locationBeingDisplayed != null) {
      await this.drawLocation(this.locationBeingDisplayed);
    }
  }

  /**
   * Draws the given Location on the Floorplan and adds it's Zones.
   * This also adds the displays for any of the tracked Assets that 
   * are in the Map if they are selected.
   */
  private async drawLocation(location: LocationInstance): Promise<void> {
    console.log(`Drawing Location: '${location?.id}/${location?.name}'`);

    // Draw the Location's Floorplan on the Map... 
    await this.mapComponent.setDisplayedLocation(location);

    // Draw Promises that we need to wait for...
    const drawPromises: Promise<void>[] = [];

    // Draw the Zones in the Location
    location?.zones?.forEach(zone => drawPromises.push(this.mapComponent.addZoneToDisplay(zone)));

    // Draw the Assets in the Location
    this.getSelectedAssetsInLocation(location)?.forEach(asset => drawPromises.push(this.mapComponent.addAssetToDisplay(asset)));

    // Wait for everything to be drawn...
    await Promise.all(drawPromises);
  }

  /**
   * Erases the current map, removing all Assets / People / Zones 
   * before removing the Floorplan
   */
  private async eraseMap(): Promise<void> {
    await this.mapComponent.removeAllAssetsFromDisplay();
    await this.mapComponent.removeAllPeopleFromDisplay();
    await this.mapComponent.removeAllZonesFromDisplay();
    await this.mapComponent.setDisplayedLocation(null);
  }

  /**
   * Returns the Assets that are selected to be tracked / displayed 
   * in the given Location.
   */
  getSelectedAssetsInLocation(location: LocationInstance): ObjectInstance[] {
    return this.assetsSelected.filter(asset => this.assetsBeingTracked.get(asset?.id)?.movementEventsEnabled && asset?.locationInstance?.id == location?.id);
  }

  /**
   * Returns the number of Assets that have been checked / selected to be drawn on a map
   * in the RHS. Used by the UI to display the total number selected at the top of the 
   * Tab.
   */
  getCountOfSelectedAssetsToBeDrawn(): number {
    return this.assetsSelected.filter(asset => this.assetsBeingTracked.get(asset?.id)?.movementEventsEnabled).length;
  }

  /**
   * Updates the following information of the displayed version of the Asset
   * with the given Asset's Data:
   *   1) Asset's Data (asset.data)
   *   2) Asset's Object Type (asset.object)
   *   3) Asset's Position
   *      a) Location (asset.locationInstance)
   *      b) Zone (asset.zoneInstance)
   *      c) Position (asset.position)
   */
  updateDisplayedAsset(asset: ObjectInstance): void {
    // Sort the Data by the Property Order value
    asset?.data?.sort((a, b) => a.property.order - b.property.order);

    // Update the information in the "Select Assets" List
    {
      const selectAsset = this.assetsToSelect.find(a => a?.id == asset?.id);
      if (selectAsset != null) {
        selectAsset.data = asset.data;
        selectAsset.object = asset.object;
        selectAsset.locationInstance = asset.locationInstance;
        selectAsset.zoneInstance = asset.zoneInstance;
        selectAsset.position = asset.position;
      }
    }

    // Update the information in the "Selected Assets" List
    {
      const selectedAsset = this.assetsSelected.find(a => a?.id == asset?.id);
      if (selectedAsset != null) {
        selectedAsset.data = asset.data;
        selectedAsset.object = asset.object;
        selectedAsset.locationInstance = asset.locationInstance;
        selectedAsset.zoneInstance = asset.zoneInstance;
        selectedAsset.position = asset.position;
      }
    }
  }

  /**
   * Called by the given Asset's Web Socket connection to handle the 
   * given Event.  This should update the Asset's data according to the 
   * Event Type.  This may also update the Map if the Asset is being 
   * drawn / tracked.
   */
   private async handleAssetWebSocketMessage(message: MessageEvent<any>, asset: ObjectInstance): Promise<void> {
    // Parse the MessageEvent data as the expected EventInstance
    const event = JSON.parse(message.data) as EventInstance;

    // Make sure the UUID of the Event Type exists so it can be compared against
    if (event?.event?.uuid == null) {
      console.warn("Ignoring unknown / unhandled event from 'Object Instance' (ASSET) Web Socket.");
      return;
    }

    // Depending on the Event Type, handle the Event...
    switch (event.event.uuid) {
      case EventUuid_AssetEnteredZone: {
        // Asset Entered a Zone, update the Zone of the Asset based on the Event
        asset.zoneInstance = event.zoneInstance;
        break;
      }
      case EventUuid_AssetExitedZone: {
        // Asset Entered a Zone, update the Zone of the Asset to NULL since it is not known
        asset.zoneInstance = null;
        break;
      }
      case EventUuid_AssetEnteredLocation: {
        // Asset Entered a Location, update the Location / Position of the Asset based on the Event
        asset.locationInstance = event.locationInstance;
        asset.position = event.point;

        // If the Asset is also being drawn, then need to update the displays
        if (this.assetsBeingTracked.get(asset?.id)?.movementEventsEnabled) {
          // Add the Asset's New Location to the Location's being displayed (only adds it if new)
          await this.addLocationToDisplay(asset.locationInstance);

          // if the Asset's New Location is the Location being displayed, draw the Asset on the Map
          if ((asset?.locationInstance?.id == this.locationBeingDisplayed?.id) && (asset.position != null)){
            await this.mapComponent.addAssetToDisplay(asset);
          }
        }
        break;
      }
      case EventUuid_AssetExitedLocation: {
        const oldLocation = asset.locationInstance;
        // Asset Exited a Location, update the Location / Position to NULL since it is not known.
        asset.locationInstance = null;
        asset.zoneInstance = null;
        asset.position = null;

        // If the Asset is also being drawn, then need to update the displays
        if (this.assetsBeingTracked.get(asset?.id)?.movementEventsEnabled) {
          if (oldLocation != null) {
            // If the Asset was previously in the displayed Location, remove it from the map
            if (oldLocation?.id == this.locationBeingDisplayed?.id) {
              await this.mapComponent.removeAssetFromDisplay(asset);
            }
            // If the Asset's old Location no longer contains any Assets being tracked / displayed, then remove the Location from the displayed list
            if (this.getSelectedAssetsInLocation(oldLocation).length == 0) {
              await this.removeLocationFromDisplay(oldLocation);
            }
          }
        }
        break;
      }
      case EventUuid_AssetMovement: {
        // Asset Moved!  Update the Asset's Postion to the position from the event
        asset.position = event.point;

        // If the Asset is also being drawn, then need to update the displays
        if (asset?.locationInstance?.id == this.locationBeingDisplayed?.id) {
          if (asset.position != null) {
            if (this.mapComponent.isAssetDisplayed(asset)) {
              await this.mapComponent.updateAssetPosition(asset);
            } else {
              await this.mapComponent.addAssetToDisplay(asset);
            }
          } else {
            if (this.mapComponent.isAssetDisplayed(asset)) {
              await this.mapComponent.removeAssetFromDisplay(asset);
            }
          }
        }
        break;
      }
      default: {
        console.warn(`Unknown Event seen for Asset.  Event: '${event?.event?.id}/${event?.event?.name}/${event?.event?.uuid}'!`);
        break;
      }
    }
  }

  /**
   * Returns a boolean indicating if the given "Search Asset"" has already been 
   * selected to be in the "Selected Assets" List. 
   */
  public isAssetToSelectSelected(asset: ObjectInstance): boolean {
    return this.assetsSelected.find(as => as?.id == asset?.id) != null;
  }

  /**
   * Called when the Row Header is clicked on an Asset in the Selected Assets List.
   * This either shows / hides the List Body depending on it's current state.
   */
  onSelectedListHeaderClicked(asset: ObjectInstance): void {
    console.log(`On Selected List Header Clicked: '${asset?.id}/${asset?.name}'!`)
    if ($(`#${this.getIdForSelectedListDisplayBody(asset)}`).hasClass('hidden')) {
      this.showSelectedListBody(asset);
    } else {
      this.hideSelectedListBody(asset);
    }
  }

  private async showSelectedListBody(asset: ObjectInstance): Promise<void> {
    // Fetch an updated data of the Object Instance from the Server...
    let freshAsset: ObjectInstance = null;
    try {
      freshAsset = await this.objectService.getObjectInstanceForId(asset.id);
    } catch (error) {
      console.error("Problem obtaining fresh Data for Asset when showing the List Body!  Stale Data will be used.", error);
    }
    if (freshAsset != null) {
      this.updateDisplayedAsset(freshAsset);
    }

    // Display the List Body
    $(`#${this.getIdForSelectedListDisplayBody(asset)}`).removeClass('hidden');
  }

  private async hideSelectedListBody(asset: ObjectInstance): Promise<void> {
    $(`#${this.getIdForSelectedListDisplayBody(asset)}`).addClass('hidden');
  }

  /**
   * Returns a boolean indicating if the given "Selected Asset" has already been
   * selected to be displayed in the map on the RHS. 
   */
  public isSelectedAssetSelectedToBeDisplayed(asset: ObjectInstance): boolean {
    return this.assetsBeingTracked.get(asset.id).movementEventsEnabled;
  }

  /**
   * Returns the ID of the HTML Element used for the Display Body 
   * for an Asset in the Selected Assets List.
   */
  getIdForSelectedListDisplayBody(instance: ObjectInstance): string {
    return `masalb_${instance?.id}`;
  }

  /**
   * Returns the ID of the HTML Container being used to display
   * the Full Details for the given Object Instance (Asset / Person).
   * Used for showing / hiding the Full Details Display.
   */
  getIdForSelectedListFullDetails(instance: ObjectInstance): string {
    return `masalfd_${instance.id}`;
  }

  /**
   * Returns the ID of the HTML Element being used to display the
   * option for displaying the Full Details when clicked. 
   * Used for showing the Full Details on click, and toggling the display
   * of this HTML Element when appropriate. 
   */
  getIdForSelectedListShowFullDetails(instance: ObjectInstance): string {
    return `masalsfd_${instance.id}`;
  }

  /**
   * Returns the ID of the HTML Element being used to display the
   * option for hiding the Full Details when clicked. 
   * Used for hiding the Full Details on click, and toggling the display
   * of this HTML Element when appropriate. 
   */
  getIdForSelectedListHideFullDetails(instance: ObjectInstance): string {
    return `masalhfd_${instance.id}`;
  }

  /**
   * Shows the Full Details display for the given Object Instance (Asset / Person).
   */
  showSelectedListFullDetails(instance: ObjectInstance): void {
    $(`#${this.getIdForSelectedListShowFullDetails(instance)}`).hide();
    $(`#${this.getIdForSelectedListHideFullDetails(instance)}`).show();
    $(`#${this.getIdForSelectedListFullDetails(instance)}`).show();
  }

  /**
   * Hides the Full Details display for the given Object Instance (Asset / Person).
   */
  hideSelectedListFullDetails(instance: ObjectInstance): void {
    $(`#${this.getIdForSelectedListHideFullDetails(instance)}`).hide();
    $(`#${this.getIdForSelectedListShowFullDetails(instance)}`).show();
    $(`#${this.getIdForSelectedListFullDetails(instance)}`).hide();
  }

  /**
   * Returns the String URL for the <img>'s "src" property used
   * to display the image for the given Object Instance
   */
  public getImageSource(objectInstance: ObjectInstance): string {
    return this.imageService.getImageSourceForObjectInstance(objectInstance);
  }

  /**
   * Returns a boolean indicating if the Asset Image is present.
   * Used in the HTML to display the Image or not. 
   */
  public isImagePresent(asset: ObjectInstance): boolean {
    if (asset.image != null) {
      return true;
    }
    return false;
  }

  /**
   * Returns the String URL for the <img>'s "src" property used
   * to display the icon for the given Object Instance
   */
  public getIconSource(objectInstance: ObjectInstance): string {
    return this.imageService.getIconSourceForObjectInstance(objectInstance);
  }

  /*
   * Returns a boolean indicating if the "Full Details" section is needed for the given Object Instance.
   * It is only needed if the Object Instance has Property Data for a Property that isn't flagged as "Main Display"
   */
  isFullDetailsNeeded(instance: ObjectInstance): boolean {
    return instance?.data?.find(datum => datum?.property?.mainDisplay == false) != null;
  }

  /**
   * Returns a boolean indicating if the given AssetUI has any Functions that are exposed via
   * it's Asset Type that can be called / executed.  Used to display the Functions as Actions
   * in the Assets Tab when the Asset is expanded.
   */
  isContainsFunctions(instance: ObjectInstance): boolean {
    return instance?.object?.functions?.length > 0;
  }

  /**
   * Called when a Function is clicked on in the Actions dropdown on an Object Instance.  The corresponding
   * Object Instance and the Function Key for the Function that was clicked are passed as parameters. 
   */
  openFunctionDialog(instance: ObjectInstance, functionKey: string): void {
    this.instanceFunctionDialogComponent.setInstanceAndFunction(instance, functionKey);
    this.instanceFunctionDialogComponent.show();
  }

  /**
   * Handler for the ObjectInstanceFunctionDialogComponent which gets called on a successful Function
   * execution with an updated version of the Asset the function was performed on.
   * 
   * This replaces the ObjectInstance in the UI List with the up-to-date version so that changes
   * to the Property Data can be reflected in the UI.
   */
  onFunctionCompletedSuccessfully(instance: ObjectInstance): void {
    // Update the instance in the displayed list so that the data changes can be reflected there
    this.updateDisplayedAsset(instance);

    // Hide the Instance Function Dialog Component
    this.instanceFunctionDialogComponent.hide();
  }

  /**
   * Called when the "Edit Data" Action is clicked in the Action(s) dropdown on an Object Instance. 
   * Displays the ObjectInstanceDataDialog to allow the User to edit the Data for the given ObjectInstance. 
   */
  openDataDialog(instance: ObjectInstance): void {
    this.instanceDataDialogComponent.setInstance(instance);
    this.instanceDataDialogComponent.show();
  }

  /**
   * Handler for the InstanceDataDialogComponent which gets called on a successful "Update Data"
   * execution with an updated version of the Asset whose Data was updated.
   * 
   * This updates the version in the UI List with the information from the returned after update version
   * so that the updated Property Data can be reflected in the UI.
   */
  onDataUpdatedSuccessfully(instance: ObjectInstance): void {
    // Update the instance in the displayed list so that the data changes can be reflected there
    this.updateDisplayedAsset(instance);

    // Hide the Instance Data Dialog Component
    this.instanceDataDialogComponent.hide();
  }

  /**
   * Returns a unique ID that is used for the Popup Dialog for the 
   * given Instance (Asset / Person).
   */
   getIdForPopup(asset: ObjectInstance): string {
    return `maAssetPopup_${asset?.id}`;
  }

  /**
   * Gets the Hide Emitter for the given Instance (Asset / Person).
   * These are used to "hide" the Popup Dialog for the Instances.
   */
  getHideEmitter(asset: ObjectInstance): EventEmitter<boolean> {
    return this.assetsBeingTracked.get(asset?.id)?.popupHideEventEmitter;
  }

  /**
   * Called when an Instance's Icon (Asset / Person) is clicked on the Map.
   */
  public onAssetClickedOnMap(event: InstanceClickedEvent): void {
    console.log(`onInstanceClicked()!  Asset: '${event?.instance?.id}/${event?.instance?.name}'`);

    // Hide the Popup Dialog if it is currently being displayed...
    this.getHideEmitter(event?.instance)?.emit(true);
 
    // Show the Popup Dialog at the mouse click
    $(`#${this.getIdForPopup(event?.instance)}`)?.css('left', `${event.event.stageX + $('#locatetab').outerWidth(true)}px`);
    $(`#${this.getIdForPopup(event?.instance)}`)?.css('top', `${event.event.stageY + $('#locationsWell').outerHeight(true)}px`);
    $(`#${this.getIdForPopup(event?.instance)}`)?.show();

    /*
     * The below piece of code is needed since the top level "role='dialog'" <div> element used for the Dialog
     * is being padded with some extra invisible space underneath the Dialog.  This is preventing click events from
     * being detected on Icons that are visible underneath the Dialog since it was being blocked by the invisible part.
     * This resizes the outer "role='dialog'" <div> element to that of the inner "role='document'" <div> element. 
     * Hacky, but works.
     */
    $(`#${this.getIdForPopup(event?.instance)}`)?.css('height', $(`#${this.getIdForPopup(event?.instance)}_Document`)?.height() + 1);
  }

  /**
   * Refreshes the "selectedAssetsToDisplay" ObjectInstance[] based on the currently values
   * in the "assetsSelected" ObjectInstance[] and the current "Selected Assets" Search
   * Values. These are the Assets that should be shown in the "Selected Assets" List
   */
  private refreshDisplayedSelectedAssetsInList(): void {
    const assets: ObjectInstance[] = [];
    const searchValue = this.selectedAssetsSearchValue;
    const searchOption = this.selectedAssetsSearchOption;

    if (searchValue.length == 0) {
      assets.push(...this.assetsSelected);
    } else {
      // Searching Assets, only display the ones that should be based on the search criteria
      assets.push(...this.assetsSelected.filter(sa => this.shouldAssetBeDisplayedInList(sa, searchOption, searchValue)));
    }
    this.selectedAssetsToDisplay = assets;
  }

  /**
   * Returns a boolean indicating if the given Asset should be displayed in the "Selected Assets"
   * List based on the given search criteria.
   */
  private shouldAssetBeDisplayedInList(asset: ObjectInstance, searchOption: SearchOption, searchValue: string): boolean {
    // If the Asset is being tracked, then it should be in the List
    if (this.assetsBeingTracked?.get(asset?.id)?.movementEventsEnabled) {
      return true;
    }

    // Instance isn't being tracked, then it needs to pass the Search Criteria
    switch (searchOption?.searchType) {
      case SearchType.NAME: {
        /*
         * Search by Name
         * Returns true if the search value is anywhere in the Instance's Name (case in-sensitive). 
         */
        return asset.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
      }
      case SearchType.PROPERTY: {
        /*
         * Search by Property
         * Returns true if the Instance Data for the Property being searched by 
         * and if the data matches criteria depending on the search value:
         *  1) Search Value = "?" 
         *     As long as Data exists and is specified for the Property, the 
         *     Instance should be shown so true will be returned.
         *  2) Anything Else
         *     Depends on the Property Type: 
         *       A) INTEGER
         *          Returns true if the search value is the same numerically as the 
         *          matching data value
         *       B) STRING / Anything Else
         *          Returns true if the search value is anywhere in the matching
         *          data value (case in-sensitive). 
         */
        // Find any matching Instance Data for the Property specified in the search
        const dataForProperty = asset?.data?.filter(datum => datum?.property?.id == searchOption?.property?.id);
        
        // If no data was found for the Property, then the Instance should not be shown.
        if (dataForProperty.length == 0) {
          return false;
        }

        /*
         * 1) If Search Value is "?", then as long as data is specified that 
         * contains a value, return true.
         */
        if (searchValue == '?') {
          return dataForProperty.find(datum => datum?.value?.trim()?.length > 0) != null;
        }

        /*
         * 2) Anything Else, search based on the Property Type of the Property specified
         * in the search criteria. 
         */
        switch (searchOption?.property?.type) {
          case PropertyType.INTEGER: {
            /* 
             * A) INTEGER - Return true if the search value is the same numerically as the 
             *    matching data value
             */
            return dataForProperty.find(datum => Number(datum?.value) == Number(searchValue)) != null;
          }
          case PropertyType.STRING: 
          default: {
            /*
             * B) STRING / Anything Else - Return true if the search value is anywhere in 
             *    the matching data value (case in-sensitive). 
             */
            return dataForProperty.find(datum => datum?.value?.toLowerCase()?.indexOf(searchValue?.toLowerCase()) > -1) != null;
          }
        }
      }
      default: {
        console.warn(`Unknown Search Type: '${searchOption?.searchType}'!  Assets will pass search and be in the list.`);
        return true;
      }
    }
  }

  /**
   * Called by the UI when the "X" Delete Option is selected for the given 
   * Stored Asset List.
   * 
   * This stores a reference to the List selected, then displays the 
   * SimpleConfirmDialog to make sure the User wanted to deleted the List.
   * The "onConfirmDialogCancelClicked" and "onConfirmDialogConfirmClicked"
   * methods would then be used to continue this process.
   */
  onDeleteClickedForStoredAssetList(assetList: StoredAssetListItem): void {
    this.assetListBeingDeleted = assetList;
    this.simpleConfirmDialogComponent.setMessage(`Are you sure you want to delete list: '${assetList?.name}'?`);
    this.simpleConfirmDialogComponent.showDialog();
  }

  /**
   * Background process for deleting a Stored Asset List.  This removes it from 
   * the "storedAssetList" array variable then saves that variable in the cookie.
   */
  private async deleteStoredAssetList(assetList: StoredAssetListItem): Promise<void> {
    this.storedAssetList = this.storedAssetList.filter(storedList => storedList?.name != assetList?.name);
    this.saveStoredAssetList();
    this.isDeletingStoredAssetList = false;
  }

  /**
   * Saveds the "storedAssetList" array variable in the cookie.
   */
  private saveStoredAssetList(): void {
    const value = JSON.stringify(this.storedAssetList);
    console.log("Saving Stored Asset Lists Vale: " + value);
    this.cookieService.set('page[monitor-assets]-key[saved-assets-list]', value, 1, '/', null, false, 'Lax');
  }

  /**
   * Returns a boolean indicating if any Assets are currently in the "Selected Assets"
   * List.  This is used by the UI to disable some things.
   */
  isAnyAssetSelected(): boolean {
    return this.assetsSelected.length > 0;
  }

  /**
   * Returns a boolean indicating if there are any Stored Asset Lists currently
   * saved in the cookie.  This is used by the UI to disable some things.
   */
  isAnySavedStoredListItemPresent(): boolean {
    return this.storedAssetList.length > 0;
  }

  /**
   * Called by the UI when the "SAVE CURRENT LIST" Button is clicked.
   * Kicks off the "Save Asset List" background process if needed.  This 
   * starts by showing the SaveAssetListDialogComponent.
   */
  onSaveCurrentListClicked(): void {
    if (this.isAnyAssetSelected() && !this.isSavingCurrentAssetList && !this.isLoadingAssetList) {
      this.isSavingCurrentAssetList = true;
      this.saveAssetListDialogComponent.setUiEnabled(true);
      this.saveAssetListDialogComponent.showDialog(this.lastLoadedOrSavedAssetList?.name);
    }
  }

  /**
   * Called when the SaveAssetListDialogComponent's "Save" Button is clicked.
   * Performs the "Save" Logic for the Asset List. 
   */
  async onSaveAssetListDialogSaveClicked(name: string): Promise<void> {
    this.saveAssetListDialogComponent.setUiEnabled(false);

    // Create a New StoredAssetListItem for the List being saved
    const newListItem = { name: name, assetIds: []} as StoredAssetListItem;
    this.assetsSelected.forEach(asset => newListItem.assetIds.push(asset?.id));

    /*
     * Build a new StoredAssetListItem[] to be used as the Asset List.
     * Add the List being saved at the top, then follow by any previously
     * stored Asset List that doesn't match the name. (Same name rewrites)
     */
    const newList: StoredAssetListItem[] = [];
    newList.push(newListItem);
    newList.push(...this.storedAssetList.filter(assetList => assetList?.name != name));
    this.storedAssetList = newList;

    // Set the reference to the last loaded / saved Asset List to the saved one. (Used to re-populate the "Save Asset List" Dialog's Name)
    this.lastLoadedOrSavedAssetList = newListItem;

    // Save the newly created Asset List, Hide the Dialog and update the flag
    this.saveStoredAssetList();
    this.saveAssetListDialogComponent.hideDialog();
    this.isSavingCurrentAssetList = false;
  } 

  /**
   * Called when the SaveAssetListDialogComponent's "Cancel" Button is clicked.
   * Closes the Dialog and cancels the save.
   */
  async onSaveAssetListDialogCancelClicked(): Promise<void> {
    this.saveAssetListDialogComponent.hideDialog();
    this.isSavingCurrentAssetList = false;
  }

  /**
   * Called by the UI when a Saved Asset List is clicked.
   * This kicks off the "Load Asset List" background process if needed.
   */
  onAssetListClicked(assetList: StoredAssetListItem): void {
    console.log(`On Asset List Clicked: '${assetList?.name}'`);
    if (!this.isLoadingAssetList && !this.isLoadingAssetList) {
      this.isLoadingAssetList = true;
      this.loadAssetList(assetList);
    }
  }

  /**
   * "Load Asset List" Background Process.
   * This loads the Asset's for all the Asset Ids in the given Saved Asset List
   * and then adds any that are retrieved to the "Selected Assets" List for tracking.
   */
  private async loadAssetList(assetList: StoredAssetListItem): Promise<void> {
    // Remove all the currently selected Assets as they are to be replaced with the saved list
    this.removeAllSelectedAssets();

    // Load all the Assets from the List 
    const promises: Promise<void>[] = [];
    assetList?.assetIds?.forEach(assetId => promises.push(this.loadAndDisplayAsset(assetId)));
    if (promises.length > 0) {
      await Promise.all(promises);
    }

    // Set the reference to the last loaded / saved Asset List to the loaded one. (Used to re-populate the "Save Asset List" Dialog's Name)
    this.lastLoadedOrSavedAssetList = assetList;

    // Reset the Loading Flag back to false since it's done
    this.isLoadingAssetList = false;
  }

  /**
   * Loads the Asset by calling the REST API to obtain it's information using 
   * the given ID, and if one was returned adds it to the "Selected Assets" List
   * before returning.
   */
  private async loadAndDisplayAsset(assetId: number): Promise<void> {
    console.log(`Loading and Displaying Asset: ${assetId}`);
    // Call the REST API to load the Asset for the Asset ID
    let asset: ObjectInstance = null;
    try {
      asset = await this.objectService.getObjectInstanceForId(assetId);
    } catch (error) {
      console.error(`Problem obtaining Asset for Asset ID: ${assetId}!`, error);
    }

    // If an Asset was obtained, add it to the "Selected Assets" List
    if (asset != null) {
      await this.addSelectAssetToAssetList(asset, false);
    }
  }

  /**
   * Removes all Assets in the "Selected Assets" List from that List.
   */
  private async removeAllSelectedAssets(): Promise<void> {
    const assetsToRemove: ObjectInstance[] = [];
    assetsToRemove.push(...this.assetsSelected);

    const promises: Promise<void>[] = [];
    assetsToRemove.forEach(asset => promises.push(this.removeSelectAssetFromAssetList(asset)));
    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  /**
   * Called when a User clicks the "Confirm" option on the SimpleConfirmDialog.
   * This deletes the Asset List from the array being stored and closes
   * the SimpleConfirmDialog.
   */
  async onConfirmDialogConfirmClicked(): Promise<void> {
    if (this.assetListBeingDeleted != null) {
      this.isDeletingStoredAssetList = true;
      await this.deleteStoredAssetList(this.assetListBeingDeleted);
    }
    this.assetListBeingDeleted = null;
    this.simpleConfirmDialogComponent.hideDialog();
  }

  /**
   * Called when a User clicks the "Cancel" option on the SimpleConfirmDialog.
   * This just closes the SimpleConfirmDialog.
   */
  onConfirmDialogCancelClicked(): void {
    console.log(`ON CONFIRM DIALOG CANCEL CLICKED!  Asset List: '${this.assetListBeingDeleted?.name}'`);
    this.assetListBeingDeleted = null;
    this.simpleConfirmDialogComponent.hideDialog();
  }

    async onPageChange(pageNumber?: number): Promise<void> {
      // Validate page number
      if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
        return;
      }

      // Avoid redundant reloads
      if (this.currentPage === pageNumber) {
        return;
      }

      this.currentPage = pageNumber;

      // Fetch the requested page from the server
      this.isSearchingAssetsToSelect = true;
      await this.loadAssetsToSelectPage(pageNumber);
      this.isSearchingAssetsToSelect = false;
    }

}

/**
 * Class designed to hold the Web Socket subscription information
 * for the Assets / People being tracked.  This also holds the
 * "Hide" Event Emitter used for the Popup Dialogs since it will
 * only be needed if the Instances are actively tracked.
 */
class WsConnection {
  /** Web Socket Components used for the connection */
  wsSubject: Subject<any>;
  wsSubscription: Subscription;

  /** Popup Hide Emitter used in UI Handling.  Stored here for convienence since it is only used when tracked. */
  popupHideEventEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  /** Flag indicating if the Movement Events have been enabled over the web socket or not */
  movementEventsEnabled = false;

  /**
   * Enables the Movement Events across the Web Socket (if needed)
   */
  enableMovementEvents(): void {
    const message: MonitorAssetsWsControlMessage = { send_movement_events: true };
    this.wsSubject.next(message);
    this.movementEventsEnabled = true;
  }

  /**
   * Disables the Movement Events across the Web Socket (if needed)
   */
  disableMovementEvents(): void {
    const message: MonitorAssetsWsControlMessage = { send_movement_events: false };
    this.wsSubject.next(message);
    this.movementEventsEnabled = false;
  }

  /**
   * Closes the WS Connection
   */
  close() {
    if (this.wsSubject != null) {
      this.wsSubject.unsubscribe();
      this.wsSubject = null;
    }
    if (this.wsSubscription != null) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = null;
    }
    this.movementEventsEnabled = false;
  }
}

/**
 * Different Types of Searches
 */
enum SearchType {
  /** Search by Instance Name */
  NAME = "NAME",
  /** Search by Property Data Value */
  PROPERTY = "PROPERTY"
}

/**
 * Search Option for Instances (Assets / People)
 */
class SearchOption {
  /** Search Type - NAME / PROPERTY */
  searchType: SearchType;
  /** Property being search by if the Search Type is PROPERTY */
  property: ObjectProperty;

  constructor(property?: ObjectProperty) {
    this.property = property;
    this.searchType = this.property == null? SearchType.NAME : SearchType.PROPERTY;
  }
}

/**
 * Structure of the control message sent across the Monitor Assets Web Socket connection
 * used to enable / disable the Movement Events.
 */
interface MonitorAssetsWsControlMessage {
  send_movement_events: boolean;
}

interface StoredAssetListItem {
  name: string;
  assetIds: number[];
}
